/**
 * Roles — `/countries/:cc/admin/roles`. Custom-roles design §1.6.
 *
 * AUTHORED (no Figma exists for the admin surface); assembled from existing
 * primitives in the `TaxGroupsPage` idiom: a stating alert, a create card, and the
 * table. Role management sits BEFORE user management in the Admin nav, which is the
 * owner's "first make an option to create a role" rendered as an ordering — and that
 * is now MEASURED (`admin-infra-nav.test.ts`) rather than asserted here, because this
 * sentence and the registry's own docblock both said it while the array said the
 * opposite until a review compared them.
 *
 * ===========================================================================
 * WHAT THIS SCREEN SAYS OUT LOUD, BECAUSE THE MODEL IS NEW
 * ===========================================================================
 * TWO POPULATIONS, ONE TABLE. Built-in roles are Appendix A's and are LOCKED —
 * renaming one orphans the notification rules that route to it by name, and editing
 * its grants would be reverted by the next seed run. Clone is the modify path, and
 * it is offered on the row rather than hidden behind an explanation of why Edit is
 * missing.
 *
 * WHAT A ROLE DOES IS VISIBLE HERE FOR THE FIRST TIME. Before this build the
 * product displayed a role's permission set on NO screen: an administrator assigning
 * "Tech Support" could not find out what that person would then be able to do. The
 * count is on the row; the keys are on the detail page.
 *
 * ===========================================================================
 * D5, APPLIED
 * ===========================================================================
 * READING is `users.manage` (everybody who assigns a role needs to see it);
 * AUTHORING is group-admin only (OD-C), because role assignment carries no
 * "you may only grant what you hold" check and authoring plus assigning would be
 * self-service escalation. A non-grp administrator therefore sees the full table and
 * NO create card and NO row actions — the capability is absent, not disabled, which
 * is this app's rule for things the server would refuse.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, Card, EmptyState, SkeletonRows, TextField } from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { ROLE_NAME_MAX_LENGTH } from '../../domain/rbac';
import { useSession } from '../../auth/session';
import { ApiRequestError } from '../../lib/api';
import { cloneRole, createRole, fetchRoles, roleKeys } from './roles-api';
import type { RoleDto, RoleWarningDto } from './roles-api';
import { NoAdminAccess, errorSentence } from './states';

function apiSentence(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) return error.message;
  return errorSentence(error, fallback);
}

/**
 * The §1.7 warnings, rendered verbatim.
 *
 * They arrive on a 200 — the role EXISTS — so this is an advisory block, never an
 * error one. Saying "created, and here is what to check" is the whole of owner
 * decision OD-F: warn, do not block.
 */
export function RoleWarnings({
  warnings,
  heading,
}: {
  warnings: readonly RoleWarningDto[];
  heading: string;
}): React.JSX.Element | null {
  if (warnings.length === 0) return null;
  return (
    <Alert tone="warning" title={heading}>
      <ul className="flex flex-col gap-1.5">
        {warnings.map((warning) => (
          <li key={warning.code} className="text-body13">
            {warning.message}
            <span className="ml-1 font-mono text-mono text-fg-secondary">
              ({warning.permissions.join(', ')})
            </span>
          </li>
        ))}
      </ul>
    </Alert>
  );
}

export function RolesListPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, isGroupAdmin } = useSession();
  const queryClient = useQueryClient();

  const permitted = isGroupAdmin || can(countryCode, 'users.manage');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<RoleWarningDto[]>([]);
  const [lastWritten, setLastWritten] = useState<string | null>(null);

  const roles = useQuery({
    queryKey: roleKeys.list(),
    queryFn: ({ signal }) => fetchRoles(signal),
    enabled: permitted,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: roleKeys.root() });
    // The user screens' role picker reads this list; a role authored here must be
    // assignable without a reload.
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const create = useMutation({
    // Created with NO permissions on purpose: the grant matrix lives on the detail
    // page, and a create form carrying 36 checkboxes would be a second editor to
    // keep in step with the first. Name it here, grant it there.
    mutationFn: () =>
      createRole({
        name: name.trim(),
        description: description.trim() === '' ? undefined : description.trim(),
        permissions: [],
      }),
    onSuccess: (result) => {
      setName('');
      setDescription('');
      setActionError(null);
      setWarnings(result.warnings);
      setLastWritten(result.role.name);
      invalidate();
    },
    onError: (error) => {
      setWarnings([]);
      setActionError(apiSentence(error, 'The role was not created.'));
    },
  });

  const clone = useMutation({
    mutationFn: (role: RoleDto) => cloneRole(role.id),
    onSuccess: (result) => {
      setActionError(null);
      setWarnings(result.warnings);
      setLastWritten(result.role.name);
      invalidate();
    },
    onError: (error) => {
      setWarnings([]);
      setActionError(apiSentence(error, 'The role was not copied.'));
    },
  });

  if (!permitted) return <NoAdminAccess />;

  const rows = roles.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Roles"
        subtitle="What each role can do, and the roles you have added yourself"
      />

      <Alert tone="info" title="Built-in roles are locked, and copying is how you change one">
        The seven built-in roles come from the permission matrix the system is seeded
        from. Renaming one would break the notification rules that route to it by name,
        and editing its permissions would be undone by the next seed. Copy it instead —
        the copy is yours to rename, re-grant and assign.
      </Alert>

      {actionError === null ? null : (
        <Alert tone="danger" title="That did not work">
          {actionError}
        </Alert>
      )}

      <RoleWarnings
        warnings={warnings}
        heading={
          lastWritten === null
            ? 'Saved — worth checking'
            : `'${lastWritten}' was saved — worth checking`
        }
      />

      {isGroupAdmin ? (
        <Card title="Add a role">
          <div className="flex flex-wrap items-end gap-3">
            <TextField
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={ROLE_NAME_MAX_LENGTH}
              placeholder="Audit (read only)"
            />
            <TextField
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={200}
              placeholder="What this role is for"
            />
            <Button
              onClick={() => create.mutate()}
              disabled={name.trim() === '' || create.isPending}
            >
              Add role
            </Button>
          </div>
          <p className="mt-2 text-label text-fg-secondary">
            The new role starts with no permissions. Open it to choose what it can do.
          </p>
        </Card>
      ) : null}

      <Card title="All roles">
        {roles.isPending ? (
          <SkeletonRows rows={4} label="Loading roles" />
        ) : roles.isError ? (
          <Alert tone="danger" title="The roles could not be loaded">
            {apiSentence(roles.error, 'Try again in a moment.')}
          </Alert>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck aria-hidden="true" className="size-5" />}
            title="No roles"
            description="Not even the built-in ones — run `pnpm seed`."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] border-collapse text-body13">
              <thead>
                <tr className="border-b border-line text-left text-label text-fg-secondary">
                  <th className="py-2 pr-3 font-medium">Role</th>
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 pr-3 font-medium">Permissions</th>
                  <th className="py-2 pr-3 font-medium">People</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {rows.map((role) => (
                  <tr key={role.id} className="crf-register-row border-b border-line last:border-0">
                    <td className="py-2 pr-3">
                      <Link
                        to={`/countries/${countryCode}/admin/roles/${String(role.id)}`}
                        className="text-fg underline-offset-2 hover:underline"
                      >
                        {role.name}
                      </Link>
                      {role.description === null ? null : (
                        <div className="text-label text-fg-secondary">{role.description}</div>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <span className="text-label text-fg-secondary">
                        {role.origin === 'seeded' ? 'Built-in' : 'Custom'}
                      </span>
                    </td>
                    <td className="py-2 pr-3">{role.permissions.length}</td>
                    {/*
                      The holder count is what makes the delete refusal predictable: a
                      role with people on it cannot be removed, and the number says how
                      many would have to move first.
                    */}
                    <td className="py-2 pr-3">{role.holders}</td>
                    <td className="py-2 text-right">
                      {isGroupAdmin ? (
                        <Button
                          variant="ghost"
                          onClick={() => clone.mutate(role)}
                          disabled={clone.isPending}
                          title={
                            role.origin === 'seeded'
                              ? 'Built-in roles cannot be changed. This makes a copy you can edit.'
                              : 'Make an editable copy of this role.'
                          }
                        >
                          Copy
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-label text-fg-secondary">
        Changing what a role can do takes effect for the people who hold it the next time
        they sign in — permissions are resolved at login, not on every request.
      </p>

    </div>
  );
}
